"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Target,
  AlertTriangle,
  Shield,
  TrendingUp,
  MessageSquare,
  Loader2,
  DollarSign,
  BarChart3,
  Activity,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  getFinancialRatiosYoY,
  getMultiYearData,
  calculateFinancialRatios,
  formatCurrency,
} from "../lib/textron-data-processor";
import { useEngagement } from "@/contexts/EngagementContext";
import { getCompanyName } from "../lib/textron-data-processor";

interface MaterialityData {
  planningMateriality?: number;
  revisedMateriality?: number;
  performanceMateriality?: number;
  clearlyTrivialThreshold?: number;
}

interface RiskSummary {
  total: number;
  higher: number;
  lower: number;
  significant: number;
  nrpmm: number;
  notYetAssessed: number;
}

interface ControlSummary {
  totalControls: number;
  manualControls: number;
  automatedControls: number;
  uniqueRisksCovered: number;
}

interface FinancialRatios {
  netProfitRatio?: number;
  debtorDays?: number;
  creditorDays?: number;
  operatingCycle?: number;
}

const COLORS = ["#4da3ff", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#6b7280"];

interface EngagementOverviewProps {
  setActiveSection?: (section: string) => void;
}

export function EngagementOverview({ setActiveSection }: EngagementOverviewProps = {}) {
  const { currentEngagement } = useEngagement();
  const [loading, setLoading] = useState(true);
  const [materialityData, setMaterialityData] = useState<MaterialityData>({});
  const [riskSummary, setRiskSummary] = useState<RiskSummary>({
    total: 0,
    higher: 0,
    lower: 0,
    significant: 0,
    nrpmm: 0,
    notYetAssessed: 0,
  });
  const [controlSummary, setControlSummary] = useState<ControlSummary>({
    totalControls: 0,
    manualControls: 0,
    automatedControls: 0,
    uniqueRisksCovered: 0,
  });
  const [financialRatios, setFinancialRatios] = useState<FinancialRatios>({});
  const [companyName, setCompanyName] = useState<string>("");
  const [reviewCommentsStats, setReviewCommentsStats] = useState({
    totalComments: 0,
    totalReplies: 0,
    openComments: 0,
  });

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        // Get company name
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");


        // Load all data in parallel
        await Promise.all([
          loadMaterialityData(name),
          loadRiskSummary(),
          loadControlSummary(),
          loadFinancialRatios(),
          loadReviewCommentsStats(),
        ]);
      } catch (error) {
        console.error("Error loading engagement overview data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [currentEngagement]);

  const loadMaterialityData = async (companyName: string) => {
    try {
      const planningAPI = (window as any).planningAPI;
      if (!planningAPI?.loadResponses) {
        console.warn("Planning API not available");
        return;
      }

      const sharePointResponses = await planningAPI.loadResponses({
        companyName,
        sectionKey: "materiality",
      });

      if (!sharePointResponses?.success || !sharePointResponses.data) {
        console.warn("Failed to load materiality data");
        return;
      }

      const responses = sharePointResponses.data.responses || sharePointResponses.data;

      // Check if revised materiality is used
      const m9Response = responses["M-9"];
      const useRevisedMateriality = m9Response?.subAnswer === "Yes";

      let planningMateriality = 0;
      let revisedMateriality = 0;
      let performanceMateriality = 0;
      let clearlyTrivialThreshold = 0;

      if (useRevisedMateriality) {
        // Get revised materiality
        const revisedMaterialityResponse = responses["revisedMateriality"];
        if (revisedMaterialityResponse) {
          revisedMateriality = parseFloat(revisedMaterialityResponse.determinedMateriality || "0") || 0;
          performanceMateriality = parseFloat(revisedMaterialityResponse.performanceMateriality || "0") || 0;
          clearlyTrivialThreshold = parseFloat(revisedMaterialityResponse.trivialThreshold || "0") || 0;
        }
        // Get planning materiality from M-3
        const m3Response = responses["M-3"];
        if (m3Response?.textResponse) {
          const textValue = typeof m3Response.textResponse === "string"
            ? m3Response.textResponse
            : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
          planningMateriality = parseFloat(textValue) || 0;
        }
      } else {
        // Use planning materiality values
        const m3Response = responses["M-3"];
        if (m3Response?.textResponse) {
          const textValue = typeof m3Response.textResponse === "string"
            ? m3Response.textResponse
            : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
          planningMateriality = parseFloat(textValue) || 0;
        }

        const m5Response = responses["M-5"];
        if (m5Response?.overridePerformanceMateriality !== undefined) {
          performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality.toString()) || 0;
        }

        const m8Response = responses["M-8"];
        if (m8Response?.textResponse) {
          const textValue = typeof m8Response.textResponse === "string"
            ? m8Response.textResponse
            : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
          clearlyTrivialThreshold = parseFloat(textValue) || 0;
        }
      }

      setMaterialityData({
        planningMateriality,
        revisedMateriality: useRevisedMateriality ? revisedMateriality : undefined,
        performanceMateriality,
        clearlyTrivialThreshold,
      });
    } catch (error) {
      console.error("Error loading materiality:", error);
    }
  };

  const loadRiskSummary = async () => {
    try {
      // Load Sections.json to get valid workspaces
      let validWorkspaces = new Set<string>();
      if ((window as any).sharePointAPI?.downloadSectionsJson) {
        try {
          const sectionsRes = await (window as any).sharePointAPI.downloadSectionsJson();
          if (sectionsRes.success && sectionsRes.data) {
            const sectionsData = sectionsRes.data;
            if (sectionsData.Planning) {
              Object.keys(sectionsData.Planning).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            if (sectionsData.Execution) {
              Object.keys(sectionsData.Execution).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            if (sectionsData.ConcludingProcedures) {
              Object.keys(sectionsData.ConcludingProcedures).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
          }
        } catch (e) {
          console.error("Error loading Sections.json:", e);
        }
      }

      // Load ROMMs from Libraries_Romm.json
      let romms: any[] = [];
      if ((window as any).sharePointAPI?.readRommLibrary) {
        try {
          const rommRes = await (window as any).sharePointAPI.readRommLibrary();
          if (rommRes.success && rommRes.data?.romm_library) {
            romms = Array.isArray(rommRes.data.romm_library)
              ? rommRes.data.romm_library
              : [];
          }
        } catch (e) {
          console.error("Error loading ROMM library:", e);
        }
      }

      // Filter ROMMs by valid workspaces - only include risks whose workspace exists in Sections.json
      romms = romms.filter((romm) => {
        const workspace = romm.workspace || "";
        const workspaceArray = Array.isArray(workspace)
          ? workspace
          : workspace
            ? [workspace]
            : [];
        // Only include ROMMs where at least one workspace in the ROMM's workspace array exists in Sections.json
        return workspaceArray.some((ws: string) => validWorkspaces.has(ws));
      });

      // Count assessments
      const summary: RiskSummary = {
        total: romms.length,
        higher: 0,
        lower: 0,
        significant: 0,
        nrpmm: 0,
        notYetAssessed: 0,
      };

      romms.forEach((romm) => {
        const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
        if (assessment.includes("higher")) {
          summary.higher++;
        } else if (assessment.includes("lower")) {
          summary.lower++;
        } else if (assessment.includes("significant")) {
          summary.significant++;
        } else if (assessment.includes("nrpmm")) {
          summary.nrpmm++;
        } else if (!assessment || assessment === "not yet assessed" || assessment === "") {
          summary.notYetAssessed++;
        }
      });

      setRiskSummary(summary);
    } catch (error) {
      console.error("Error loading risk summary:", error);
    }
  };

  const loadControlSummary = async () => {
    try {
      // Load Controls from Libraries_RCM.json
      let controls: any[] = [];
      if ((window as any).sharePointAPI?.downloadRcmJson) {
        try {
          const rcmRes = await (window as any).sharePointAPI.downloadRcmJson();
          if (rcmRes.success && rcmRes.data) {
            const data = rcmRes.data;
            const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
            const entries = Object.entries(librariesRcm) as [string, any][];

            controls = entries.map(([controlId, entry]) => ({
              controlId,
              nature: entry.Nature || entry.subtype || entry.nature || "Unknown",
              rommIds: Array.isArray(entry.romm) ? entry.romm.map(String) : [],
            }));
          }
        } catch (e) {
          console.error("Error loading RCM controls:", e);
        }
      }

      // Calculate summary based on Nature field
      const summary: ControlSummary = {
        totalControls: controls.length,
        manualControls: controls.filter((c) => {
          const nature = (c.nature || "").toLowerCase();
          return nature.includes("manual");
        }).length,
        automatedControls: controls.filter((c) => {
          const nature = (c.nature || "").toLowerCase();
          return nature.includes("automated") || nature.includes("it");
        }).length,
        uniqueRisksCovered: new Set(
          controls.flatMap((c) => c.rommIds)
        ).size,
      };

      setControlSummary(summary);
    } catch (error) {
      console.error("Error loading control summary:", error);
    }
  };

  const loadFinancialRatios = async () => {
    try {
      const [multiYearDataResult, ratiosYoYResult] = await Promise.all([
        getMultiYearData("combined"),
        getFinancialRatiosYoY("combined"),
      ]);

      const latestYearData = multiYearDataResult[multiYearDataResult.length - 1];
      const ratios = await calculateFinancialRatios(latestYearData);

      // Find specific ratios
      const netProfitRatio = ratios.find((r) =>
        r.name.toLowerCase().includes("net profit")
      );
      const debtorDays = ratios.find((r) =>
        r.name.toLowerCase().includes("debtor days") ||
        r.name.toLowerCase().includes("debtors days")
      );
      const creditorDays = ratios.find((r) =>
        r.name.toLowerCase().includes("creditor days") ||
        r.name.toLowerCase().includes("creditors days")
      );
      const operatingCycle = ratios.find((r) =>
        r.name.toLowerCase().includes("operating cycle")
      );

      // Get latest values from YoY data
      const getLatestValue = (ratioName: string) => {
        const yoyData = ratiosYoYResult[ratioName];
        if (yoyData && yoyData.length > 0) {
          return yoyData[yoyData.length - 1]?.value || null;
        }
        return null;
      };

      setFinancialRatios({
        netProfitRatio: netProfitRatio
          ? getLatestValue(netProfitRatio.name) || undefined
          : undefined,
        debtorDays: debtorDays
          ? getLatestValue(debtorDays.name) || undefined
          : undefined,
        creditorDays: creditorDays
          ? getLatestValue(creditorDays.name) || undefined
          : undefined,
        operatingCycle: operatingCycle
          ? getLatestValue(operatingCycle.name) || undefined
          : undefined,
      });
    } catch (error) {
      console.error("Error loading financial ratios:", error);
    }
  };

  const loadReviewCommentsStats = async () => {
    try {
      if ((window as any).reviewCommentsAPI?.downloadAllComments) {
        const result = await (window as any).reviewCommentsAPI.downloadAllComments();
        if (result.success && result.data) {
          const comments = result.data.comments || [];

          // Total Comments = number of top-level review comment objects
          const totalComments = comments.length;

          // Count all replies recursively (including nested replies)
          const countReplies = (commentList: any[]): number => {
            let count = 0;
            for (const comment of commentList) {
              if (comment.replies && Array.isArray(comment.replies) && comment.replies.length > 0) {
                count += comment.replies.length;
                count += countReplies(comment.replies); // Recursive for nested replies
              }
            }
            return count;
          };

          const totalReplies = countReplies(comments);

          // Open Comments = comments where replies array is empty
          const openComments = comments.filter(
            (comment: any) => !comment.replies || !Array.isArray(comment.replies) || comment.replies.length === 0
          ).length;

          setReviewCommentsStats({
            totalComments,
            totalReplies,
            openComments,
          });
        } else {
          setReviewCommentsStats({
            totalComments: 0,
            totalReplies: 0,
            openComments: 0,
          });
        }
      } else {
        setReviewCommentsStats({
          totalComments: 0,
          totalReplies: 0,
          openComments: 0,
        });
      }
    } catch (error) {
      console.error("Error loading review comments stats:", error);
      setReviewCommentsStats({
        totalComments: 0,
        totalReplies: 0,
        openComments: 0,
      });
    }
  };

  // Prepare risk summary chart data
  const riskChartData = [
    { name: "Higher", value: riskSummary.higher, color: COLORS[0] },
    { name: "Lower", value: riskSummary.lower, color: COLORS[1] },
    { name: "Significant", value: riskSummary.significant, color: COLORS[2] },
    { name: "NRPMM", value: riskSummary.nrpmm, color: COLORS[3] },
    {
      name: "Not Yet Assessed",
      value: riskSummary.notYetAssessed,
      color: COLORS[4],
    },
  ].filter((item) => item.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-white/80">Loading engagement overview...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-key p-6 animate-in fade-in duration-500">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-left space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-glass-blue border border-blue-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></div>
            <span className="text-xs font-medium text-blue-300">Engagement Overview</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Engagement Overview</h1>
            <p className="text-muted-foreground text-lg">
              Comprehensive summary for {companyName || "the engagement"}
            </p>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Risks */}
          <Card className="glass-card hover:bg-glass-red/10 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex flex-col h-full justify-between">
                <div>
                  <div className="p-3 rounded-lg bg-red-500/10 w-fit mb-4 group-hover:bg-red-500/20 transition-colors">
                    <AlertTriangle className="h-6 w-6 text-red-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Risks</h3>
                  <span className="text-3xl font-bold text-white">{riskSummary.total}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Identified Across Engagement</p>
              </div>
            </CardContent>
          </Card>

          {/* Total Controls */}
          <Card className="glass-card hover:bg-glass-green/10 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex flex-col h-full justify-between">
                <div>
                  <div className="p-3 rounded-lg bg-green-500/10 w-fit mb-4 group-hover:bg-green-500/20 transition-colors">
                    <Shield className="h-6 w-6 text-green-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Controls</h3>
                  <span className="text-3xl font-bold text-white">{controlSummary.totalControls}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Implemented Controls</p>
              </div>
            </CardContent>
          </Card>

          {/* Planning Materiality */}
          <Card className="glass-card hover:bg-glass-blue/10 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex flex-col h-full justify-between">
                <div>
                  <div className="p-3 rounded-lg bg-blue-500/10 w-fit mb-4 group-hover:bg-blue-500/20 transition-colors">
                    <Target className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Planning Materiality</h3>
                  <span className="text-2xl font-bold text-white truncate" title={materialityData.planningMateriality ? formatCurrency(materialityData.planningMateriality) : "N/A"}>
                    {materialityData.planningMateriality
                      ? formatCurrency(materialityData.planningMateriality)
                      : "N/A"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Overall Materiality Threshold</p>
              </div>
            </CardContent>
          </Card>

          {/* Unique Risks Covered */}
          <Card className="glass-card hover:bg-glass-purple/10 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex flex-col h-full justify-between">
                <div>
                  <div className="p-3 rounded-lg bg-purple-500/10 w-fit mb-4 group-hover:bg-purple-500/20 transition-colors">
                    <Activity className="h-6 w-6 text-purple-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Risks Covered</h3>
                  <span className="text-3xl font-bold text-white">{controlSummary.uniqueRisksCovered}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">By Internal Controls</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Materiality Card */}
          <Card className="glass-card">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-glass-blue flex items-center justify-center">
                  <Target className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Materiality</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Planning & Performance Metrics</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-glass-blue">
                  <p className="text-xs text-white/60 mb-1.5">Planning Materiality</p>
                  <p className="text-base font-semibold text-white">
                    {materialityData.planningMateriality
                      ? formatCurrency(materialityData.planningMateriality)
                      : "N/A"}
                  </p>
                </div>
                {materialityData.revisedMateriality !== undefined && (
                  <div className="p-4 rounded-lg bg-glass-blue">
                    <p className="text-xs text-white/60 mb-1.5">Revised Materiality</p>
                    <p className="text-base font-semibold text-white">
                      {formatCurrency(materialityData.revisedMateriality)}
                    </p>
                  </div>
                )}
                <div className="p-4 rounded-lg bg-glass-green">
                  <p className="text-xs text-white/60 mb-1.5">Performance Materiality</p>
                  <p className="text-base font-semibold text-white">
                    {materialityData.performanceMateriality
                      ? formatCurrency(materialityData.performanceMateriality)
                      : "N/A"}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-glass-orange">
                  <p className="text-xs text-white/60 mb-1.5">Trivial Threshold</p>
                  <p className="text-base font-semibold text-white">
                    {materialityData.clearlyTrivialThreshold
                      ? formatCurrency(materialityData.clearlyTrivialThreshold)
                      : "N/A"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Risk Summary Card */}
          <Card className="glass-card">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-glass-red flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Risk Summary</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Assessment Distribution</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-2xl font-bold text-white">{riskSummary.total}</p>
                  <p className="text-xs text-white/60">Total Risks Assessed</p>
                </div>
                {riskChartData.length > 0 && (
                  <div className="w-36 h-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={riskChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={60}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {riskChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0];
                              return (
                                <div className="rounded-lg border border-white/10 bg-black/90 p-3 text-white shadow-xl backdrop-blur-sm">
                                  <p className="font-semibold text-sm">{data.name}</p>
                                  <p className="text-blue-400 font-bold">{data.value} risks</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-xs text-white/70 font-medium">Higher</span>
                  <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                    {riskSummary.higher}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-xs text-white/70 font-medium">Lower</span>
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                    {riskSummary.lower}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-xs text-white/70 font-medium">Significant</span>
                  <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                    {riskSummary.significant}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-xs text-white/70 font-medium">NRPMM</span>
                  <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                    {riskSummary.nrpmm}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10 col-span-2">
                  <span className="text-xs text-white/70 font-medium">Not Yet Assessed</span>
                  <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30">
                    {riskSummary.notYetAssessed}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Control Summary Card */}
          <Card className="glass-card">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-glass-green flex items-center justify-center">
                  <Shield className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Control Summary</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Manual vs Automated</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-5 rounded-lg bg-glass-green">
                  <p className="text-3xl font-bold text-white mb-1">
                    {controlSummary.totalControls}
                  </p>
                  <p className="text-sm text-white/60">Total Controls</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-2xl font-semibold text-white mb-1">
                      {controlSummary.manualControls}
                    </p>
                    <p className="text-xs text-white/60">Manual</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-2xl font-semibold text-white mb-1">
                      {controlSummary.automatedControls}
                    </p>
                    <p className="text-xs text-white/60">Automated</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-glass-purple">
                  <p className="text-xl font-semibold text-white mb-1">
                    {controlSummary.uniqueRisksCovered}
                  </p>
                  <p className="text-xs text-white/60">Unique Risks Covered</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Ratios Card */}
          <Card className="glass-card">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-glass-purple flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Financial Ratios</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Latest Period Metrics</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60 mb-1.5">Net Profit Ratio</p>
                  <p className="text-lg font-semibold text-white">
                    {financialRatios.netProfitRatio !== undefined
                      ? `${financialRatios.netProfitRatio.toFixed(2)}%`
                      : "N/A"}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60 mb-1.5">Debtor Days</p>
                  <p className="text-lg font-semibold text-white">
                    {financialRatios.debtorDays !== undefined
                      ? `${Math.round(financialRatios.debtorDays)} days`
                      : "N/A"}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60 mb-1.5">Creditor Days</p>
                  <p className="text-lg font-semibold text-white">
                    {financialRatios.creditorDays !== undefined
                      ? `${Math.round(financialRatios.creditorDays)} days`
                      : "N/A"}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60 mb-1.5">Operating Cycle</p>
                  <p className="text-lg font-semibold text-white">
                    {financialRatios.operatingCycle !== undefined
                      ? `${Math.round(financialRatios.operatingCycle)} days`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Review Comments Card */}
          <Card className="glass-card">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-glass-orange flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Review Comments</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Comments & Replies Summary</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-5 rounded-lg bg-glass-orange">
                  <p className="text-3xl font-bold text-white mb-1">
                    {reviewCommentsStats.totalComments}
                  </p>
                  <p className="text-sm text-white/60">Total Comments</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-2xl font-semibold text-white mb-1">
                      {reviewCommentsStats.totalReplies}
                    </p>
                    <p className="text-xs text-white/60">Total Replies</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-2xl font-semibold text-white mb-1">
                      {reviewCommentsStats.openComments}
                    </p>
                    <p className="text-xs text-white/60">Open Comments</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
